import React, { useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useRulesetResourceChoices } from '../hooks/useRulesetResourceChoices';
import {
  IRulesetAction,
  IRulesetSchemaAction,
  IRulesetSchemaFieldChoice,
  isActionParamApplicable,
} from '../../../types/ruleset-types';

interface ActionsSectionProps {
  actions: IRulesetAction[];
  schemaActions: IRulesetSchemaAction[];
  onChange: (actions: IRulesetAction[]) => void;
  disabled?: boolean;
  dataPoolId: string;
}

const DEFAULT_ACTION_TYPE = 'set_training_set';
const getActionType = (action: IRulesetSchemaAction) => (action as any).type || action.name;

const ActionsSection: React.FC<ActionsSectionProps> = ({
  actions,
  schemaActions,
  onChange,
  disabled = false,
  dataPoolId,
}) => {
  // Get available actions (not yet added)
  const addedActionTypes = actions.map((a) => a.type);
  const availableActions = schemaActions
    .filter((schema) => !addedActionTypes.includes(getActionType(schema)))
    .sort((left, right) => {
      const leftType = getActionType(left);
      const rightType = getActionType(right);

      if (leftType === DEFAULT_ACTION_TYPE) {
        return -1;
      }

      if (rightType === DEFAULT_ACTION_TYPE) {
        return 1;
      }

      return 0;
    });

  const handleAddAction = (schemaAction: IRulesetSchemaAction) => {
    const actionType = getActionType(schemaAction);
    onChange([...actions, { type: actionType }]);
  };

  const handleRemoveAction = (actionType: string) => {
    onChange(actions.filter((a) => a.type !== actionType));
  };

  const handleValueChange = (actionType: string, paramName: string, value: string | number | null) => {
    const newActions = actions.map((a) =>
      a.type === actionType ? { ...a, [paramName]: value } : a
    );
    onChange(newActions);
  };

  if (schemaActions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No actions available.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Added actions */}
      {actions.map((action) => {
        const schemaAction = schemaActions.find((s) => getActionType(s) === action.type);
        if (!schemaAction) return null;

        return (
          <ActionRow
            key={action.type}
            action={action}
            schemaAction={schemaAction}
            onValueChange={(paramName, value) => handleValueChange(action.type, paramName, value)}
            onRemove={() => handleRemoveAction(action.type)}
            disabled={disabled}
            dataPoolId={dataPoolId}
          />
        );
      })}

      {/* Available actions to add */}
      {availableActions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {availableActions.map((schemaAction) => (
            <Button
              key={getActionType(schemaAction)}
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleAddAction(schemaAction)}
              disabled={disabled}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '13px',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              data-cy={`btn-add-action-${getActionType(schemaAction)}`}
            >
              {schemaAction.label}
            </Button>
          ))}
        </Box>
      )}

      {/* Empty state */}
      {actions.length === 0 && availableActions.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No actions configured.
        </Typography>
      )}
    </Box>
  );
};

interface ActionRowProps {
  action: IRulesetAction;
  schemaAction: IRulesetSchemaAction;
  onValueChange: (paramName: string, value: string | number | null) => void;
  onRemove: () => void;
  disabled: boolean;
  dataPoolId: string;
}

interface ActionParamFieldProps {
  actionType: string;
  paramName: string;
  paramConfig: any;
  currentValue: string | number | undefined;
  onChange: (value: string | number | null) => void;
  disabled: boolean;
  dataPoolId: string;
}

const ActionParamField: React.FC<ActionParamFieldProps> = ({
  actionType,
  paramName,
  paramConfig,
  currentValue,
  onChange,
  disabled,
  dataPoolId,
}) => {
  const hasDynamicChoices = !!paramConfig?.choices_source;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const lastSelectedOptionRef = useRef<IRulesetSchemaFieldChoice | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const resourceChoices = useRulesetResourceChoices({
    source: paramConfig?.choices_source,
    dataPoolId,
    enabled: hasDynamicChoices && !!dataPoolId,
    search: debouncedSearch || undefined,
  });

  const isLoadingChoices = hasDynamicChoices && debouncedSearch !== searchInput;

  const choices: IRulesetSchemaFieldChoice[] = hasDynamicChoices
    ? resourceChoices
    : paramConfig?.choices || [];

  if (hasDynamicChoices) {
    let selectedOption = choices.find((c) => c.value === currentValue) || null;
    if (selectedOption) {
      lastSelectedOptionRef.current = selectedOption;
    } else if (currentValue && lastSelectedOptionRef.current?.value === currentValue) {
      selectedOption = lastSelectedOptionRef.current;
    }

    const inputValue = searchInput || selectedOption?.label || '';

    return (
      <Autocomplete
        size="small"
        options={choices}
        getOptionLabel={(option) => option.label}
        value={selectedOption}
        inputValue={inputValue}
        onChange={(_, newValue) => {
          if (newValue) {
            lastSelectedOptionRef.current = newValue;
          }
          setSearchInput('');
          setDebouncedSearch('');
          onChange(newValue?.value ?? null);
        }}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === 'input') {
            setSearchInput(newInputValue);
          } else if (reason === 'clear') {
            setSearchInput('');
            setDebouncedSearch('');
          }
        }}
        loading={isLoadingChoices}
        disabled={disabled}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select..."
            variant="outlined"
            data-cy={`autocomplete-action-${actionType}-${paramName}`}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingChoices ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{ sx: { maxHeight: 300 } }}
        noOptionsText={searchInput ? 'No matches found' : 'Type to search...'}
      />
    );
  }

  return (
    <FormControl size="small" fullWidth>
      <Select
        value={currentValue === null || currentValue === undefined ? '' : String(currentValue)}
        onChange={(e: SelectChangeEvent<string>) => {
          const val = e.target.value;
          if (val === '') {
            onChange(null);
          } else {
            const choice = choices.find((c) => String(c.value) === val);
            onChange(choice?.value ?? null);
          }
        }}
        disabled={disabled}
        displayEmpty
        variant="outlined"
        data-cy={`select-action-${actionType}-${paramName}`}
        renderValue={(selected) => {
          if (!selected) return <span style={{ color: '#999' }}>Select...</span>;
          const choice = choices.find((c) => String(c.value) === selected);
          return choice?.label || selected;
        }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
      >
        {choices.map((choice) => (
          <MenuItem key={String(choice.value)} value={String(choice.value)}>
            {choice.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const ActionRow: React.FC<ActionRowProps> = ({
  action,
  schemaAction,
  onValueChange,
  onRemove,
  disabled,
  dataPoolId,
}) => {
  const actionType = getActionType(schemaAction);

  const declaredParams = schemaAction.params
    ? Array.isArray(schemaAction.params)
      ? schemaAction.params.map((p) => ({ name: p.name, config: p }))
      : Object.entries(schemaAction.params as Record<string, any>).map(([name, config]) => ({
          name,
          config,
        }))
    : [];

  // The schema advertises every param an action can take; only some of them
  // apply to the action as configured (`reason` belongs to EXCLUDED alone).
  const params = declaredParams.filter((param) => isActionParamApplicable(action, param.name));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        p: 1.5,
        backgroundColor: '#fff',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Action type label */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: '#c77700',
          textTransform: 'uppercase',
          fontSize: '11px',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
        }}
      >
        {schemaAction.label}
      </Typography>

      {/* Param selectors */}
      {params.map((param) => (
        <Box key={param.name} sx={{ flex: '1 1 180px', minWidth: 180, maxWidth: 300 }}>
          <ActionParamField
            actionType={actionType}
            paramName={param.name}
            paramConfig={param.config}
            currentValue={action[param.name] as string | number | undefined}
            onChange={(value) => onValueChange(param.name, value)}
            disabled={disabled}
            dataPoolId={dataPoolId}
          />
        </Box>
      ))}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Remove button */}
      <IconButton
        size="small"
        onClick={onRemove}
        disabled={disabled}
        sx={{
          color: 'text.disabled',
          '&:hover': {
            color: 'text.secondary',
          },
        }}
        data-cy={`btn-remove-action-${actionType}`}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

export default ActionsSection;
